import { Button, Skeleton } from '@mantine/core';
import { motion, stagger, type Variants } from 'motion/react';

import classes from './StepperCard.module.css';
import type { SlideOption } from '#plugin-chat/components/tools/ask-user-question/schema.ts';
import { useStreamingSuspense } from '#plugin-chat/lib/ui/streaming-suspense.tsx';

const optionMotionVariants: Variants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.2,
      delayChildren: stagger(0.4),
      when: 'beforeChildren',
    },
  },
};

const optionMotionChildVariants: Variants = {
  hidden: {
    opacity: 0,
    y: -20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
    },
  },
};

export interface OptionSlideProps {
  options: SlideOption[];
  onOptionClick: (id: string) => void;
  isStreaming?: boolean;
}

/**
 * Skeleton fallback for a streaming option button.
 */
function _OptionSkeleton() {
  return (
    <motion.div className={classes.actionButtonContainer} variants={optionMotionChildVariants}>
      <Skeleton height={42} radius="sm" />
    </motion.div>
  );
}

interface OptionButtonProps {
  option: SlideOption;
  onOptionClick: (id: string) => void;
  isStreaming?: boolean;
}

/**
 * Single option button that suspends while streaming.
 * Wrapped in Suspense by parent.
 */
function OptionButton({ option, onOptionClick, isStreaming = false }: OptionButtonProps) {
  // Suspend if streaming and option doesn't have required fields
  useStreamingSuspense(option, isStreaming, ['id', 'label']);

  return (
    <motion.div className={classes.actionButtonContainer} variants={optionMotionChildVariants}>
      <Button color="dark-accent" h="100%" onClick={() => onOptionClick(option.id)} radius="sm">
        <span className={classes.optionContent}>
          <span className={classes.optionLabel}>{option.label}</span>
          {option.description && <span className={classes.optionDescription}>{option.description}</span>}
        </span>
      </Button>
    </motion.div>
  );
}

/** Radio-style option slide with staggered animation. */
export function OptionSlide({ options, onOptionClick, isStreaming }: OptionSlideProps) {
  return (
    <motion.div animate="visible" className={classes.options} initial="hidden" variants={optionMotionVariants}>
      {options
        ?.filter((option) => !!option.id)
        ?.map((option) => (
          <OptionButton isStreaming={isStreaming} key={option.id} onOptionClick={onOptionClick} option={option} />
        ))}
    </motion.div>
  );
}
// {
//   /* <Suspense fallback={<OptionSkeleton />} key={`${option.id}-${index}`}> */
// }
// {
//   /* </Suspense> */
// }
// // Use option.id when available (stable identity), fall back to index for streaming
// <ErrorBoundary
//   fallback={<OptionSkeleton />}
//   key={option.id ?? `streaming-${index}`}
//   onError={(error) => console.error('Option render error:', error)}
// >
//   <Suspense fallback={<OptionSkeleton />}>
//     <OptionButton isStreaming={isStreaming} onOptionClick={onOptionClick} option={option} />
//   </Suspense>
// </ErrorBoundary>
